// Finds out - reliably - whether THIS phone runs the Animora Android notification service (native/android/*.java).
//
// The server only builds notifications WITH buttons (Reply, Like, Answer, Decline) for phones that report
// pushMode = 'native-actions'. Before, that was decided with a bare `Capacitor.Plugins.AnimoraNotify` check, which is
// undefined whenever Capacitor has not created that proxy yet (plugin never registered from JS) - so a phone that DID have
// the service was still marked 'standard' and got plain notifications with no buttons, forever.
// This looks the plugin up three ways and finally asks the native side itself (isAvailable), which cannot lie.

const NAME = 'AnimoraNotify'

/** The AnimoraNotify plugin proxy, or null when this is not the Android app / the native service is not built into it. */
export function animoraNotifyPlugin(): any | null {
  if (typeof window === 'undefined') return null
  const C = (window as any).Capacitor
  if (!C || !C.isNativePlatform?.()) return null
  try { if (C.Plugins?.[NAME]) return C.Plugins[NAME] } catch { /* fall through */ }
  try { if (C.isPluginAvailable?.(NAME) && C.registerPlugin) return C.registerPlugin(NAME) } catch { /* fall through */ }
  return null
}

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T | null> =>
  Promise.race([p, new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))])

/** true if the native Animora notification service is installed. Newer builds also report serviceReady: false when the manifest entry is broken
 *  (-> stay on plain pushes, never silent ones). Older builds answer without serviceReady and are trusted, as before. */
export async function detectNativeActions(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const C = (window as any).Capacitor
  if (!C || !C.isNativePlatform?.()) return false

  // 1) the normal plugin proxy
  const plugin = animoraNotifyPlugin()
  if (plugin) {
    try {
      const r: any = await withTimeout(Promise.resolve(plugin.isAvailable?.()), 3000)
      if (r?.available === true && r?.serviceReady !== false) return true
    } catch { /* fall through to the raw bridge */ }
  }

  // 2) the raw native bridge. The JS proxy can be missing even though the Java plugin IS registered (it only exists once
  //    something calls registerPlugin from JS) - that used to leave the phone on 'standard' = plain notifications with no
  //    Reply / Like buttons. nativePromise talks to the Java side directly, so it does not depend on the proxy at all.
  try {
    if (typeof C.nativePromise === 'function') {
      const r: any = await withTimeout(Promise.resolve(C.nativePromise(NAME, 'isAvailable', {})), 3000)
      if (r?.available === true && r?.serviceReady !== false) return true
    }
  } catch { /* plugin not registered natively */ }
  return false
}
